import { useQueryClient } from '@tanstack/react-query';
import { UploadUsage, type UploadUsageType } from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';

export const useS3Api = (api: AxiosInstance) => {
  const baseUrl = `/s3`;

  const getUploadPresignedUrl = async (
    key: string,
    size: number,
    usage: UploadUsageType,
  ): Promise<{
    url: string;
    fields: Record<string, string>;
    key: string | null | undefined;
  }> => {
    const { data } = await api.get(
      `${baseUrl}/upload-presigned-url/${encodeURIComponent(key)}?size=${size}&usage=${usage}`,
    );

    return data;
  };

  const upload = async (file: File, usage: UploadUsageType) => {
    const { url, fields, key } = await getUploadPresignedUrl(file.name, file.size, usage);

    const formData = new FormData();

    Object.entries(fields).forEach(([field, value]) => {
      formData.append(field, value);
    });

    formData.append('file', file);

    // Upload the file using fetch (direct S3 upload, not through our API)
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`S3 upload of ${file.name} failed with status ${response.status}`);
    }

    return key;
  };

  const uploadAttachmentFile = (file: File) => upload(file, UploadUsage.Attachment);

  const queryClient = useQueryClient();

  const uploadImage = useErrorHandlingMutation({
    mutationFn: async (file: File) => await upload(file, UploadUsage.Image),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
  });

  const uploadAttachment = useErrorHandlingMutation({
    mutationFn: uploadAttachmentFile,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
  });

  const uploadCsvImport = useErrorHandlingMutation({
    mutationFn: async (file: File) => await upload(file, UploadUsage.CsvImport),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
  });

  return { uploadImage, uploadAttachment, uploadAttachmentFile, uploadCsvImport };
};
